import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectEntitiesByFilters } from "../../../../../shared/store/views/entityView";
import { usePermission } from "../../../../../shared/hooks/usePermission";
import { studyGroupToolsFilters, usefulToolsDashboardFilters } from "../../../../../shared/utils/entitiesFiltersUtils";
import { deleteToolThunk } from "../../../../../shared/store/redux/tool-actions";
import { deviceTypes } from "../../../../../shared/enums/deviceTypes";
import { isElementarySchool } from "../../../../../shared/utils/userDetailsUtils";
import Widget from "../../../../../shared/components/features/widget/Widget";
import UsefulTool from "../../../../../shared/components/features/usefulTool/UsefulTool";
import iconsList from "../../../../../shared/components/ui/icons/iconsList";
import ToolDotsActions from "../../../../../shared/components/features/toolsList/toolDotsActions/ToolDotsActions";
import AddToolListItem from "../../../../../shared/components/features/toolsList/addToolListItem/AddToolListItem";
import ToolAddOrUpdatePopup from "../../../../../shared/components/features/toolsList/toolAddOrUpdatePopup/ToolAddOrUpdatePopup";
import sourceToolTypeEnum from "../../../../../shared/enums/sourceToolTypeEnum";
import toolLevelType from "../../../../../shared/enums/toolLevelType";
import targetGroupType from "../../../../../shared/enums/targetGroupType";
import classes from "./UseFullToolByNmm.module.css";

const MAX_TOOLS_TO_SHOW = 6;

const UseFullToolByNmm = ({ studyGroupId }) => {

    const dispatch = useDispatch();
    const hasPermission = usePermission();
    const { texts, lang } = useSelector(state => state.translate);
    const { deviceType } = useSelector(state => state.responsive);
    const { userDetails } = useSelector(state => state.auth);
    const [popupTool, setPopupTool] = useState(null);
    const studyGroupFilters = useMemo(() => studyGroupToolsFilters([studyGroupId]), [studyGroupId]);
    const defaultTools = useSelector((state) => selectEntitiesByFilters(state, usefulToolsDashboardFilters)).filter(tool => hasPermission(tool.visibleTo)).slice(0, MAX_TOOLS_TO_SHOW);
    const studyGroupTools = useSelector((state) => selectEntitiesByFilters(state, studyGroupFilters));
    const canManageTools = !isElementarySchool(userDetails) && deviceType !== deviceTypes.mobile;

    const handleDelete = (tool) => {
        dispatch(deleteToolThunk(tool));
    };

    const toolsList = useMemo(() => {
        if (isElementarySchool(userDetails))
            return defaultTools;
        if (deviceTypes.mobile === deviceType && studyGroupTools?.length < 1)
            return defaultTools;
        return studyGroupTools
    }, [defaultTools, studyGroupTools, deviceType, userDetails]);

    return (
        <Widget
            color="#4A4A4A"
            title={texts.usefulToolsDomainTitel}
            titleColor="white"
            icon={iconsList.usefulTools}
            className={classes.UsefulToolsWidget}
        >
            <div className={classes.ToolsList}>
                {canManageTools &&
                    <AddToolListItem
                        className={classes.AddToolListItem}
                        onClick={() => setPopupTool({})}
                        accessibilityDescription={texts.addToolBtn}
                    />
                }
                {toolsList?.map((tool, index) =>
                    <div key={tool.toolId || tool.id || index} className={classes.ToolItem}>
                        <UsefulTool tool={{ ...tool, name: tool.name?.[lang] ?? tool.name }} />
                        {canManageTools &&
                            <ToolDotsActions
                                tool={{ ...tool, name: tool.name?.[lang] ?? tool.name }}
                                onEdit={(t) => setPopupTool(t)}
                                onDelete={handleDelete}
                            />
                        }
                    </div>
                )}
            </div>
            {canManageTools && popupTool !== null &&
                <ToolAddOrUpdatePopup
                    tool={popupTool}
                    onClose={() => setPopupTool(null)}
                    sourceTypeId={sourceToolTypeEnum.Teacher}
                    toolLevel={toolLevelType.StudyGroup}
                    targetGroup={targetGroupType.Student}
                    studyGroupId={studyGroupId}
                />
            }
        </Widget>
    );
};

export default UseFullToolByNmm;
